import { useEffect, useState, type FormEvent, type ReactNode } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { ApiError, patch, post, type Destination as ApiDestination, type YouTubeDualInput } from '../../api'
import { HowTo, rich, useVerticalKey } from '../VerticalInput'
import { YOUTUBE_DUAL_HELP_URL } from '../vertical'
import { LogoSlot } from '../ui'
import { t } from '../../i18n'
import { useSession } from '../../session'
import { refusalText } from '../../billing'
import { Button, Check, Icon, Note, Tag } from '../../ui'

/** Words for the center's refusals of POST /api/destinations/youtube-dual (never the raw code). */
function dualError(e: unknown, user: ReturnType<typeof useSession>['user']): string {
  if (e instanceof ApiError) {
    if (e.code === 'youtube_dual_same_key' || /youtube_dual_same_key/.test(e.message)) return t('vert.err.sameKey')
    if (e.code === 'vertical_input_off' || /vertical_input_off/.test(e.message)) return t('vert.err.inputOff')
    const plan = refusalText(e.code, user, e.data)
    if (plan) return plan
  }
  return (e as Error).message
}

/**
 * «YouTube: horizontal + vertical» (mockup D; docs/vertical.md §4; VT-07, VT-10): the ConnectWizard panel over the
 * Studio. Step 1 is the vertical input (Turn on right here when it is off), step 2 the two YouTube keys; YouTube's
 * limits said plainly (start the encoder first, the link is made in YouTube Studio); captions on the vertical
 * output are Experimental and off by default; both outputs are created in one request (POST
 * /api/destinations/youtube-dual). The keys never stay in the page after they are sent.
 */
export function YouTubeDualWizard({ streamName, phone, onClose, onDone }: { streamName?: string; phone?: boolean; onClose: () => void; onDone?: () => void }) {
  const qc = useQueryClient()
  const { user } = useSession()
  const v = useVerticalKey(streamName)
  const [main, setMain] = useState('')
  const [vert, setVert] = useState('')
  const [captions, setCaptions] = useState(false)
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')
  useEffect(() => {
    const key = (e: KeyboardEvent) => e.key === 'Escape' && !busy && onClose()
    document.addEventListener('keydown', key)
    return () => document.removeEventListener('keydown', key)
  }, [busy, onClose])
  const ready = v.on && main.trim() !== '' && vert.trim() !== '' && !/\s/.test(main.trim() + vert.trim())
  const submit = async (e: FormEvent) => {
    e.preventDefault()
    if (!ready || busy || !v.key) return
    setBusy(true)
    setErr('')
    try {
      const body: YouTubeDualInput = { stream_key_id: v.key.id, main_key: main.trim(), vertical_key: vert.trim(), enabled: true }
      const r = await post<{ horizontal: ApiDestination; vertical: ApiDestination }>('/api/destinations/youtube-dual', body)
      // Q4 (§4.5): captions on the second key only when asked for — off by default
      if (captions) await patch(`/api/destinations/${r.vertical.id}`, { captions_http: true }).catch(() => undefined)
      setMain('')
      setVert('')
      await qc.invalidateQueries({ queryKey: ['destinations'] })
      onDone?.()
      onClose()
    } catch (e2) {
      setErr(dualError(e2, user))
      setBusy(false)
    }
  }
  const steps = ['vert.yt.s1', 'vert.yt.s2', 'vert.yt.s3', 'vert.yt.s4', 'vert.yt.s5'] as const
  const field = (id: string, label: ReactNode, value: string, set: (x: string) => void) => (
    <label className="sx-field min-w-0">
      <span className="flex items-center gap-1.5 text-xs text-muted">{label}</span>
      <input data-testid={id} type="password" autoComplete="off" spellCheck={false} value={value} disabled={busy} placeholder={t('vert.yt.paste')}
        onChange={(e) => { set(e.target.value); setErr('') }}
        className="sx-input sans min-w-0 outline-none placeholder:text-muted focus:border-accent focus:shadow-focus max-md:min-h-[44px]" />
    </label>
  )
  return (
    <div data-testid="yt-dual-scrim" className={`fixed inset-0 z-40 grid animate-scrim bg-scrim ${phone ? 'items-end' : 'place-items-center p-6'}`} onClick={() => !busy && onClose()}>
      {/* DS: VT-07 — the ConnectWizard panel as a floating layer over the Studio */}
      <form role="dialog" aria-modal="true" aria-labelledby="yt-dual-title" data-testid="yt-dual" onSubmit={submit} onClick={(e) => e.stopPropagation()}
        className={`sx-ticks flex max-h-[92dvh] w-full animate-modal flex-col overflow-hidden border border-line-2 bg-panel shadow-pop ${phone ? 'rounded-t-lg' : 'max-w-[680px] rounded-lg'}`}>
        <ol className="sx-steps m-0 list-none p-0" aria-label={t('vert.yt.title')}>
          <li className={`sx-step ${v.on ? 'done' : 'cur'}`} aria-current={v.on ? undefined : 'step'}><span className="n">{v.on ? <Icon name="check" size={12} /> : 1}</span>{t('vert.yt.stepVertical')}</li>
          <li className={`sx-step ${v.on ? 'cur' : ''}`} aria-current={v.on ? 'step' : undefined}><span className="n">2</span>{t('vert.yt.stepKeys')}</li>
        </ol>
        <div className="flex min-h-0 flex-1 flex-col gap-3.5 overflow-y-auto px-6 pt-4 pb-5 max-md:px-4">
          <span className="flex items-center gap-2.5"><LogoSlot platform="youtube" /><span className="sx-label">{t('vert.yt.title').toUpperCase()}</span></span>
          <h2 id="yt-dual-title" className="m-0 text-2xl leading-[30px] font-medium max-md:text-xl">{t('vert.yt.heading')}</h2>
          <HowTo testId="yt-dual-steps" steps={steps.map((k) => rich(t(k)))} done={v.on ? [0] : []} />
          {!v.on && (
            <Button data-testid="yt-dual-vertical-on" busy={v.busy === 'on'} disabled={!!v.busy || !v.key} onClick={() => void v.turnOn()} className="self-start max-md:min-h-[44px]">
              <Icon name="plus" size={16} />{t('vert.in.turnOn')}
            </Button>
          )}
          {v.err && <span role="alert" className="text-xs text-fail">{v.err.text}</span>}
          <div className="grid gap-x-4 gap-y-3 md:grid-cols-2">
            {field('yt-dual-main', t('vert.yt.mainKey'), main, setMain)}
            {field('yt-dual-vertical', <>{t('vert.yt.vKey')} <Tag>{t('vert.tag')}</Tag></>, vert, setVert)}
          </div>
          <Note state="warn">{rich(t('vert.yt.warn'))}</Note>
          {/* VT-10: Experimental, off by default (Q4) */}
          <Check testId="yt-dual-captions" checked={captions} onChange={setCaptions} disabled={busy}
            label={<span className="inline-flex flex-wrap items-center gap-2">{t('vert.yt.captions')}<Tag>{t('vert.yt.experimental')}</Tag></span>}
            hint={t('vert.yt.captionsHint')} />
          <p className="m-0 text-xs text-text-soft">{t('vert.yt.wait')}</p>
          {err && <span role="alert" data-testid="yt-dual-error" className="inline-flex items-center gap-1 text-xs text-fail"><Icon name="state-fail" size={12} />{err}</span>}
        </div>
        <div className="flex flex-none flex-wrap items-center justify-between gap-2 border-t border-line px-6 py-3 max-md:px-4">
          <a href={YOUTUBE_DUAL_HELP_URL} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-[13px] text-accent no-underline"><Icon name="external" size={16} />{t('vert.yt.help')}</a>
          <span className="flex gap-2">
            <Button variant="ghost" type="button" data-testid="yt-dual-back" disabled={busy} onClick={onClose}>{t('common.back')}</Button>
            <Button variant="primary" type="submit" data-testid="yt-dual-add" busy={busy} disabled={!ready || busy}>{t('vert.yt.add')}</Button>
          </span>
        </div>
      </form>
    </div>
  )
}
